/**
 * The inbox, as in AgencyAnalytics: a bell with an unread count, opening a
 * drawer on the right. "All clear!" when empty; a "⋯" menu with Mark all as
 * read and Settings (the notification matrix). Opening an item marks it read
 * and follows its link. The count is polled while the tab is visible.
 */

import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';

import { BellIcon } from './BellIcon.tsx';
import { INBOX_CHANGED, inboxApi, type InboxItem } from './kpis-api.ts';

const POLL_MS = 60_000;
const MAX_BADGE = 99;

const when = new Intl.DateTimeFormat('en-ZA', {
  day: 'numeric',
  month: 'short',
  hour: '2-digit',
  minute: '2-digit',
});

function Drawer(props: {
  organisationId: string;
  onClose: () => void;
  onUnread: (count: number) => void;
}): ReactNode {
  const [items, setItems] = useState<InboxItem[] | null>(null);
  const [menu, setMenu] = useState(false);
  const panel = useRef<HTMLElement>(null);
  const { organisationId, onUnread } = props;
  useEffect(() => {
    inboxApi
      .list(organisationId)
      .then((data) => {
        setItems(data.items);
        onUnread(data.unread);
      })
      .catch(() => setItems([]));
    panel.current?.focus();
  }, [organisationId, onUnread]);

  const open = (item: InboxItem) => {
    if (!item.read_at)
      void inboxApi.read(organisationId, item.id).then((data) => onUnread(data.unread));
    if (item.link) window.location.hash = item.link.slice(1);
    props.onClose();
  };
  const readAll = () => {
    setMenu(false);
    void inboxApi.readAll(organisationId).then(() => {
      onUnread(0);
      setItems((current) =>
        (current ?? []).map((item) => ({
          ...item,
          read_at: item.read_at ?? new Date().toISOString(),
        })),
      );
    });
  };

  return (
    <aside className="ib-drawer" aria-labelledby="ib-title" tabIndex={-1} ref={panel}>
      <header className="ib-head">
        <h2 id="ib-title">Inbox</h2>
        <span className="ag-spacer" />
        <div className="ex-filter">
          <button
            type="button"
            className="kp-more"
            aria-label="Inbox options"
            aria-haspopup="menu"
            aria-expanded={menu}
            onClick={() => setMenu(!menu)}
          >
            ⋯
          </button>
          {menu && (
            <ul className="ex-pop ex-pop-list kp-menu" role="menu">
              <li role="none">
                <button type="button" role="menuitem" onClick={readAll}>
                  Mark all as read
                </button>
              </li>
              <li role="none">
                <a role="menuitem" href="#/settings" onClick={props.onClose}>
                  Settings
                </a>
              </li>
            </ul>
          )}
        </div>
        <button type="button" className="kp-close" aria-label="Close inbox" onClick={props.onClose}>
          ×
        </button>
      </header>
      {items === null && <p className="ib-empty">Loading…</p>}
      {items?.length === 0 && (
        <div className="ib-empty">
          <strong>All clear!</strong>
          <p>Alerts, approvals, comments and downloads will show up here.</p>
        </div>
      )}
      {items && items.length > 0 && (
        <ul className="ib-list">
          {items.map((item) => (
            <li key={item.id} className={item.read_at ? '' : 'ib-unread'}>
              <button type="button" onClick={() => open(item)}>
                <strong>{item.title}</strong>
                {item.body && <span>{item.body}</span>}
                <small>
                  {when.format(new Date(item.created_at))}
                  {!item.read_at && <span className="sr-only"> · unread</span>}
                </small>
              </button>
            </li>
          ))}
        </ul>
      )}
    </aside>
  );
}

export function InboxBell({ organisationId }: { organisationId: string }): ReactNode {
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const bell = useRef<HTMLButtonElement>(null);
  const poll = useCallback(() => {
    if (document.visibilityState !== 'visible') return;
    inboxApi
      .unread(organisationId)
      .then(setUnread)
      .catch(() => undefined);
  }, [organisationId]);
  useEffect(() => {
    poll();
    const timer = window.setInterval(poll, POLL_MS);
    window.addEventListener(INBOX_CHANGED, poll);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener(INBOX_CHANGED, poll);
    };
  }, [poll]);
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (event: KeyboardEvent) => event.key === 'Escape' && close();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);
  const close = () => {
    setOpen(false);
    bell.current?.focus();
  };
  const label = unread > 0 ? `Inbox, ${unread} unread` : 'Inbox';
  return (
    <>
      <button
        ref={bell}
        type="button"
        className="ag-rail-link ib-bell"
        aria-label={label}
        aria-expanded={open}
        onClick={() => setOpen(!open)}
      >
        <span className="ag-rail-icon" aria-hidden="true">
          <BellIcon size={22} />
        </span>
        Inbox
        {unread > 0 && (
          <span className="ib-badge" aria-hidden="true">
            {unread > MAX_BADGE ? `${MAX_BADGE}+` : unread}
          </span>
        )}
      </button>
      {open && <Drawer organisationId={organisationId} onClose={close} onUnread={setUnread} />}
    </>
  );
}
